import React, {useCallback, useMemo, useRef, useState} from "react";
import {read, utils} from "xlsx";
import {toast} from "react-toastify";
import {useMutation} from "@apollo/client";
import useTranslation from "../../i18n/useTranslation";
import useThemeColors from "./portal/useThemeColors";
import OrganizationImportTemplateButton from "./OrganizationImportTemplateButton";
import {
    IMPORT_ORGANIZATION_STRUCTURE,
    GET_IMPORTED_ACCOUNT_ACTIVATION_SUMMARY,
    VALIDATE_ORGANIZATION_IMPORT,
} from "./queries/GetOrganizationImportTemplate";

const SHEET_FIELDS = {
    Divisions: {
        required: ["name", "sport"],
        fields: ["externalId", "name", "description", "sport", "mountDistance", "pitchLimit", "restingGames", "gameDuration"],
    },
    Seasons: {
        required: ["name", "division", "startDate"],
        fields: ["externalId", "divisionExternalId", "division", "name", "registrationStartDate", "registrationEndDate", "startDate", "endDate", "lateRegistrationUntil", "state", "registrationFeeType", "lateRegistrationFee"],
    },
    Stages: {
        required: ["name", "season"],
        fields: ["externalId", "seasonExternalId", "season", "name", "scheduleType", "minGamesRequired", "gamesMode", "qtyTeamsQualified", "qtyTeamsAdditional"],
    },
    Groups: {
        required: ["name", "stage", "team"],
        fields: ["externalId", "stageExternalId", "stage", "name", "teamExternalId", "team", "teamAliasName"],
    },
    Teams: {
        required: ["name", "season"],
        fields: ["externalId", "name", "sport", "seasonExternalId", "season", "status", "isActive"],
    },
    Players: {
        required: ["firstName", "lastName", "season", "team", "contact"],
        fields: ["externalId", "email", "mobile", "firstName", "lastName", "dob", "gender", "divisionExternalId", "division", "seasonExternalId", "season", "teamExternalId", "team", "shirtNumber", "position", "note"],
    },
    Coaches: {
        required: ["firstName", "lastName", "season", "team", "contact"],
        fields: ["externalId", "email", "mobile", "firstName", "lastName", "dob", "gender", "divisionExternalId", "division", "seasonExternalId", "season", "teamExternalId", "team", "note"],
    },
    Referees: {
        required: ["firstName", "lastName", "contact"],
        fields: ["externalId", "email", "mobile", "firstName", "lastName", "dob", "gender", "sport", "note"],
    },
};

const FIELD_LABEL_KEYS = {
    externalId: "externalId", divisionExternalId: "divisionExternalId", division: "division", name: "name", description: "description", sport: "sport", mountDistance: "mountDistance", pitchLimit: "pitchLimit", restingGames: "restingGames", gameDuration: "gameDuration", registrationStartDate: "registrationStartDate", registrationEndDate: "registrationEndDate", startDate: "startDate", endDate: "endDate", lateRegistrationUntil: "lateRegistrationUntil", state: "state", registrationFeeType: "registrationFeeType", lateRegistrationFee: "lateRegistrationFee", seasonExternalId: "seasonExternalId", season: "season", scheduleType: "scheduleType", minGamesRequired: "minGamesRequired", gamesMode: "gamesMode", qtyTeamsQualified: "qtyTeamsQualified", qtyTeamsAdditional: "qtyTeamsAdditional", stageExternalId: "stageExternalId", stage: "stage", teamExternalId: "teamExternalId", team: "team", teamAliasName: "teamAliasName", email: "email", mobile: "mobile", firstName: "firstName", lastName: "lastName", dob: "dob", gender: "gender", shirtNumber: "shirtNumber", position: "position", note: "note", isActive: "isActive", status: "status", contact: "contact",
};

const HEADER_ALIASES = {
    externalId: ["externalid", "sourceid", "legacyid"], divisionExternalId: ["divisionexternalid", "divisionid"], division: ["division", "divisionname", "category"], name: ["name", "title"], description: ["description", "details"], sport: ["sport", "sportname"], mountDistance: ["mountdistance", "pitchingdistance"], pitchLimit: ["pitchlimit", "maxpitches"], restingGames: ["restinggames", "restgames"], gameDuration: ["gameduration", "duration"], registrationStartDate: ["registrationstartdate", "registrationstart"], registrationEndDate: ["registrationenddate", "registrationend"], startDate: ["startdate", "seasonstart", "begindate"], endDate: ["enddate", "seasonend"], lateRegistrationUntil: ["lateregistrationuntil", "lateregistration"], state: ["state", "status"], registrationFeeType: ["registrationfeetype", "feetype"], lateRegistrationFee: ["lateregistrationfee"], seasonExternalId: ["seasonexternalid", "seasonid"], season: ["season", "seasonname"], scheduleType: ["scheduletype", "stage type", "format"], minGamesRequired: ["mingamesrequired", "minimumgames"], gamesMode: ["gamesmode", "gamemode"], qtyTeamsQualified: ["qtyteamsqualified", "qualifiedteams"], qtyTeamsAdditional: ["qtyteamsadditional", "additionalteams"], stageExternalId: ["stageexternalid", "stageid"], stage: ["stage", "stagename"], teamExternalId: ["teamexternalid", "teamid"], team: ["team", "teamname"], teamAliasName: ["teamaliasname", "alias"], email: ["email", "emailaddress"], mobile: ["mobile", "phone", "phonenumber"], firstName: ["firstname", "givenname"], lastName: ["lastname", "surname", "familyname"], dob: ["dob", "dateofbirth", "birthdate"], gender: ["gender", "sex"], shirtNumber: ["shirtnumber", "jerseynumber", "number"], position: ["position", "role"], note: ["note", "notes", "comments"], isActive: ["isactive", "active"], status: ["status"],
};

const normalize = (value) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

const autoMapHeader = (header, fields) => {
    const normalizedHeader = normalize(header);
    const field = fields.find((candidate) =>
        (HEADER_ALIASES[candidate] || []).some((alias) => normalize(alias) === normalizedHeader)
    );

    return field || (normalizedHeader.startsWith("custom") ? `custom:${header}` : "");
};

const getFieldLabel = (field, t) => field.startsWith("custom:")
    ? field.slice(7)
    : t(`organizations.importFields.${FIELD_LABEL_KEYS[field] || field}`);

const getSheetConfig = (sheetName) => SHEET_FIELDS[sheetName] || {
    required: [],
    fields: [],
};

const hasContactMapping = (mapping) => {
    const mappedFields = Object.values(mapping);
    return mappedFields.includes("email") || mappedFields.includes("mobile");
};
const hasRequiredMapping = (mapping, field) => {
    if (field === "contact") return hasContactMapping(mapping);
    const mappedFields = Object.values(mapping);
    if (field === "division") return mappedFields.includes("division") || mappedFields.includes("divisionExternalId");
    if (field === "season") return mappedFields.includes("season") || mappedFields.includes("seasonExternalId");
    if (field === "stage") return mappedFields.includes("stage") || mappedFields.includes("stageExternalId");
    if (field === "team") return mappedFields.includes("team") || mappedFields.includes("teamExternalId");
    return mappedFields.includes(field);
};

export default function OrganizationImportModal({
    organizationId,
    modalId = "organization-import-modal",
    canUseFeature = true,
    onFeatureUnavailable,
}) {
    const {t} = useTranslation();
    const colors = useThemeColors();
    const fileInputRef = useRef(null);
    const [fileName, setFileName] = useState("");
    const [sheets, setSheets] = useState([]);
    const [activeSheetName, setActiveSheetName] = useState("");
    const [mappings, setMappings] = useState({});
    const [rowErrors, setRowErrors] = useState({});
    const [validationSummary, setValidationSummary] = useState(null);
    const [isValidated, setIsValidated] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [validateImport, {loading: validating}] = useMutation(VALIDATE_ORGANIZATION_IMPORT);
    const [importStructure, {loading: importing}] = useMutation(IMPORT_ORGANIZATION_STRUCTURE, {
        refetchQueries: organizationId ? [{query: GET_IMPORTED_ACCOUNT_ACTIVATION_SUMMARY, variables: {organizationId: Number(organizationId)}}] : [],
        awaitRefetchQueries: true,
    });
    const scopeSel = useMemo(() => `#${modalId}`, [modalId]);
    const fileInputId = useMemo(() => `${modalId}-file-input`, [modalId]);

    const activeSheet = useMemo(
        () => sheets.find((sheet) => sheet.name === activeSheetName) || sheets[0],
        [activeSheetName, sheets]
    );
    const activeConfig = getSheetConfig(activeSheet?.name);
    const activeMapping = mappings[activeSheet?.name] || {};
    const activeMissingRequired = activeConfig.required.filter((field) => !hasRequiredMapping(activeMapping, field));

    const reset = useCallback(() => {
        setFileName("");
        setSheets([]);
        setActiveSheetName("");
        setMappings({});
        setRowErrors({});
        setValidationSummary(null);
        setIsValidated(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, []);

    const handleFileChange = useCallback(async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
            toast.error(t("organizations.importUnsupportedFile"), {position: "top-center"});
            event.target.value = "";
            return;
        }

        setParsing(true);
        setFileName(file.name);
        setIsValidated(false);

        try {
            const workbook = read(await file.arrayBuffer(), {type: "array", cellDates: true});
            const parsedSheets = workbook.SheetNames
                .filter((name) => name.toLowerCase() !== "instructions")
                .map((name) => {
                    const rows = utils.sheet_to_json(workbook.Sheets[name], {
                        header: 1,
                        defval: "",
                        raw: false,
                    });
                    const headers = (rows[0] || []).map((header) => String(header || "").trim()).filter(Boolean);
                    const data = rows.slice(1).filter((row) => row.some((value) => String(value || "").trim() !== ""));
                    const config = getSheetConfig(name);
                    return {
                        name,
                        headers,
                        rows: data,
                        mapping: headers.reduce((result, header) => ({...result, [header]: autoMapHeader(header, config.fields)}), {}),
                    };
                })
                .filter((sheet) => sheet.headers.length);

            if (!parsedSheets.length) throw new Error(t("organizations.importNoSheets"));

            setSheets(parsedSheets);
            setActiveSheetName(parsedSheets[0].name);
            setMappings(parsedSheets.reduce((result, sheet) => ({...result, [sheet.name]: sheet.mapping}), {}));
            toast.success(t("organizations.importFileParsed"), {position: "top-center"});
        } catch (error) {
            reset();
            toast.error(error.message || t("organizations.importParseError"), {position: "top-center"});
        } finally {
            setParsing(false);
        }
    }, [reset, t]);

    const updateMapping = useCallback((header, field) => {
        setIsValidated(false);
        setMappings((previous) => ({
            ...previous,
            [activeSheet.name]: {
                ...(previous[activeSheet.name] || {}),
                [header]: field,
            },
        }));
    }, [activeSheet?.name]);

    const handleValidate = useCallback(async () => {
        const invalidSheets = sheets.filter((sheet) => {
            const config = getSheetConfig(sheet.name);
            const mapping = mappings[sheet.name] || {};
            return config.required.some((field) => !hasRequiredMapping(mapping, field));
        });

        if (invalidSheets.length) {
            setActiveSheetName(invalidSheets[0].name);
            setRowErrors({});
            setValidationSummary(null);
            setIsValidated(false);
            toast.error(t("organizations.importMissingRequiredFields"), {position: "top-center"});
            return false;
        }

        try {
            const {data} = await validateImport({
                variables: {
                    organizationId: Number(organizationId),
                    sheets: sheets.map(({name, headers, rows}) => ({name, headers, rows})),
                    mappings,
                },
            });
            const result = data?.validateOrganizationImport;
            setRowErrors(result?.summary?.row_errors || {});
            setValidationSummary(result?.summary || null);
            if (result?.success) {
                setIsValidated(true);
                toast.success(t("organizations.importMappingReady"), {position: "top-center"});
                return true;
            } else {
                setIsValidated(false);
                toast.error((result?.errors || []).join(" ") || t("organizations.importRowValidationError"), {position: "top-center"});
                return false;
            }
        } catch (error) {
            toast.error(error.message || t("organizations.importValidationError"), {position: "top-center"});
            return false;
        }
    }, [mappings, organizationId, sheets, t, validateImport]);

    const closeModal = useCallback(() => {
        if (window.$ && typeof window.$(`#${modalId}`)?.modal === "function") {
            window.$(`#${modalId}`).modal("hide");
        }
    }, [modalId]);

    const handleImportStructure = useCallback(async () => {
        const valid = await handleValidate();
        if (!valid) return;
        try {
            const {data} = await importStructure({
                variables: {
                    organizationId: Number(organizationId),
                    sheets: sheets.map(({name, headers, rows}) => ({name, headers, rows})),
                    mappings,
                },
            });
            const result = data?.importOrganizationStructure;
            if (result?.success) {
                toast.success(t("organizations.importStructureSuccess"), {position: "top-center"});
                reset();
                closeModal();
            } else {
                toast.error((result?.errors || []).join(" "), {position: "top-center"});
            }
        } catch (error) {
            toast.error(error.message || t("organizations.importStructureError"), {position: "top-center"});
        }
    }, [closeModal, handleValidate, importStructure, mappings, organizationId, reset, sheets, t]);

    return (
        <>
            <button
                type="button"
                className="btn kbola-importTrigger"
                style={{
                    borderRadius: 12,
                    padding: "9px 14px",
                    fontWeight: 900,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    background: colors.primary,
                    color: colors.textOnPrimary,
                    border: "1px solid rgba(20,21,26,0.14)",
                    boxShadow: "0 5px 12px rgba(0,0,0,0.10)",
                }}
                data-toggle={canUseFeature ? "modal" : undefined}
                data-target={`#${modalId}`}
                onClick={(event) => {
                    if (!canUseFeature) {
                        event.preventDefault();
                        onFeatureUnavailable?.();
                    }
                }}
                disabled={!organizationId}
            >
                <i className="fas fa-file-import" />
                {t("organizations.importExistingData")} {!canUseFeature && <span className="badge ml-1" style={{background: "rgba(255,255,255,.2)"}}>Enterprise</span>}
            </button>

            <div className="modal fade kbola-importModal" id={modalId} tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-dialog modal-xl" role="document">
                    <div className="modal-content kbola-modalContent">
                        <div className="modal-header kbola-modalHeader">
                            <div className="kbola-modalTitleWrap">
                                <span className="kbola-pill kbola-pillBrand"><i className="fas fa-database" /> {t("organizations.importDataBadge")}</span>
                                <h4 className="modal-title kbola-modalTitle">{t("organizations.importExistingData")}</h4>
                            </div>
                            <button type="button" className="close kbola-closeBtn" data-dismiss="modal" aria-label={t("organizations.closeImport")}><span aria-hidden="true">×</span></button>
                        </div>
                        <div className="modal-body kbola-modalBody">
                            <div className="kbola-infoCard">
                                <div className="kbola-infoIcon"><i className="fas fa-shield-alt" /></div>
                                <div>
                                    <div className="kbola-infoTitle">{t("organizations.importExistingData")}</div>
                                {t("organizations.importMappingHint")}
                                </div>
                            </div>
                            <div className="kbola-uploadCard">
                                <div className="kbola-uploadTop kbola-uploadTopRow">
                                    <div className="kbola-uploadTitle">{t("organizations.chooseImportFile")}</div>
                                    <div className="kbola-uploadActions">
                                        <span className="kbola-uploadHint">{t("organizations.importSupportedFiles")}</span>
                                        <OrganizationImportTemplateButton
                                            organizationId={organizationId}
                                            className="btn kbola-templateBtn"
                                        />
                                    </div>
                                </div>
                                <input id={fileInputId} ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} disabled={parsing} className="kbola-fileNative" />
                                <div className="kbola-fileShell">
                                    <label htmlFor={fileInputId} className="kbola-fileBtn mb-0">
                                        <i className="fas fa-upload" /> {parsing ? t("organizations.parsingImportFile") : t("organizations.chooseFile")}
                                    </label>
                                    <div className="kbola-fileMeta">
                                        <span className={fileName ? "kbola-fileName" : "kbola-filePlaceholder"}>{fileName || t("organizations.noImportFile")}</span>
                                        <div className="kbola-fileSubhint">{t("organizations.importFileHint")}</div>
                                    </div>
                                    {fileName && <button type="button" className="kbola-fileClear" onClick={reset} aria-label={t("organizations.clearImport")}><i className="fas fa-times" /></button>}
                                </div>
                            </div>

                            {sheets.length > 0 && (
                                <>
                                    <div className="kbola-sectionLabel">{t("organizations.importSheets")}</div>
                                    <div className="d-flex flex-wrap mb-3 kbola-sheetTabs">
                                        {sheets.map((sheet) => (
                                            <button key={sheet.name} type="button" className={`btn kbola-sheetTab ${sheet.name === activeSheet?.name ? "kbola-sheetTabActive" : ""}`} onClick={() => setActiveSheetName(sheet.name)}>
                                                {sheet.name} ({sheet.rows.length}){rowErrors[sheet.name]?.length ? " · " + rowErrors[sheet.name].length + " " + t("organizations.importRowErrorsShort") : ""}
                                            </button>
                                        ))}
                                    </div>
                                    {validationSummary && (
                                        <div className={`kbola-importSummary mb-3 ${Object.keys(rowErrors).length ? "kbola-importSummaryWarning" : "kbola-importSummarySuccess"}`}>
                                            <div className="kbola-importSummaryTitle"><i className={`fas ${Object.keys(rowErrors).length ? "fa-exclamation-triangle" : "fa-check-circle"}`} /> {t("organizations.importReviewSummary")}</div>
                                            <div className="kbola-importSummaryStats">
                                                <span><strong>{validationSummary.sheet_count || 0}</strong> {t("organizations.importSummarySheets")}</span>
                                                <span><strong>{validationSummary.row_count || 0}</strong> {t("organizations.importSummaryRows")}</span>
                                                <span><strong>{Object.values(rowErrors).reduce((total, sheetErrors) => total + sheetErrors.length, 0)}</strong> {t("organizations.importSummaryErrors")}</span>
                                            </div>
                                        </div>
                                    )}
                                    {rowErrors[activeSheet?.name]?.length > 0 && (
                                        <div className="kbola-rowErrors mb-3">
                                            <div className="kbola-rowErrorsTitle"><i className="fas fa-exclamation-triangle" /> {t("organizations.importRowErrors")}</div>
                                            {rowErrors[activeSheet.name].map((item) => (
                                                <div className="kbola-rowErrorItem" key={activeSheet.name + "-" + item.row}>
                                                    <strong>{t("organizations.importRowLabel", {row: item.row})}</strong>
                                                    <span>{item.errors.join(" ")}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="table-responsive kbola-mappingTableWrap">
                                        <table className="table table-sm mb-0 kbola-mappingTable">
                                            <thead className="kbola-thead">
                                            <tr><th>{t("organizations.sourceColumn")}</th><th>{t("organizations.kbolaField")}</th><th>{t("organizations.sampleValue")}</th></tr>
                                            </thead>
                                            <tbody className="kbola-tbody">
                                            {activeSheet.headers.map((header) => (
                                                <tr key={header}>
                                                    <td><strong>{header}</strong></td>
                                                    <td>
                                                        <select className="form-control kbola-control" value={activeMapping[header] || ""} onChange={(event) => updateMapping(header, event.target.value)}>
                                                            <option value="">{t("organizations.ignoreColumn")}</option>
                                                            {activeConfig.fields.map((field) => <option key={field} value={field}>{getFieldLabel(field, t)}</option>)}
                                                            {activeMapping[header]?.startsWith("custom:") && <option value={activeMapping[header]}>{getFieldLabel(activeMapping[header], t)}</option>}
                                                        </select>
                                                    </td>
                                                    <td>{String(activeSheet.rows[0]?.[activeSheet.headers.indexOf(header)] || "")}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="kbola-previewHeader">
                                        <div className="kbola-sectionLabel mb-0">{t("organizations.importPreview")}</div>
                                        <span>{t("organizations.importPreviewLimit")}</span>
                                    </div>
                                    <div className="table-responsive kbola-mappingTableWrap">
                                        <table className="table table-sm mb-0 kbola-mappingTable">
                                            <thead className="kbola-thead">
                                            <tr>
                                                <th>#</th>
                                                {activeSheet.headers.map((header) => <th key={header}>{getFieldLabel(activeMapping[header] || header, t)}</th>)}
                                            </tr>
                                            </thead>
                                            <tbody className="kbola-tbody">
                                            {activeSheet.rows.slice(0, 10).map((row, index) => (
                                                <tr key={`${activeSheet.name}-preview-${index}`}>
                                                    <td><strong>{index + 2}</strong></td>
                                                    {activeSheet.headers.map((header, columnIndex) => <td key={`${header}-${index}`}>{String(row[columnIndex] || "")}</td>)}
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className={`kbola-requiredAlert ${activeMissingRequired.length ? "kbola-requiredAlertWarning" : "kbola-requiredAlertSuccess"}`}>
                                        {activeMissingRequired.length
                                            ? `${t("organizations.missingRequiredFields")}: ${activeMissingRequired.map((field) => getFieldLabel(field, t)).join(", ")}`
                                            : t("organizations.requiredFieldsMapped")}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer kbola-modalFooter">
                           <button type="button" className="btn btn-default kbola-secondaryBtn" onClick={() => { reset(); closeModal(); }}>{t("organizations.closeImport")}</button>
                           <button type="button" className="btn btn-default kbola-secondaryBtn" onClick={handleValidate} disabled={!sheets.length || parsing || validating || importing}><i className="fas fa-check-circle" /> {validating ? t("organizations.validatingMapping") : t("organizations.validateMapping")}</button>
                            <button type="button" className="btn btn-kbola kbola-primaryBtn" onClick={handleImportStructure} disabled={!sheets.length || !isValidated || parsing || validating || importing}><i className="fas fa-upload" /> {importing ? t("organizations.importingStructure") : t("organizations.importStructure")}</button>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                ${scopeSel} { font-family: 'Poppins', sans-serif; }
                ${scopeSel} .modal-dialog { width: calc(100vw - 2rem); max-width: 1500px; }
                ${scopeSel} .kbola-modalContent { border-radius: 18px; overflow: hidden; border: 1px solid rgba(0,0,0,0.08); box-shadow: 0 18px 44px rgba(0,0,0,0.18); }
                ${scopeSel} .kbola-modalHeader { padding: 14px 16px; background: ${colors.primary}; border-bottom: 1px solid rgba(0,0,0,0.06); }
                ${scopeSel} .kbola-modalTitleWrap { display: flex; align-items: center; gap: 10px; min-width: 0; }
                ${scopeSel} .kbola-modalTitle { font-size: 16px; font-weight: 900; color: ${colors.textOnPrimary}; }
                ${scopeSel} .kbola-importTrigger { border-radius: 12px; padding: 9px 14px; font-weight: 900; display: inline-flex; align-items: center; gap: 8px; background: ${colors.primary}; color: ${colors.textOnPrimary}; border: 1px solid rgba(20,21,26,0.14); box-shadow: 0 5px 12px rgba(0,0,0,0.10); }
                ${scopeSel} .kbola-importTrigger:hover { filter: brightness(0.96); color: ${colors.textOnPrimary}; }
                ${scopeSel} .kbola-closeBtn { color: ${colors.textOnPrimary}; opacity: 1; }
                ${scopeSel} .kbola-modalBody { background: #fff; max-height: calc(100vh - 210px); overflow-y: auto; padding: 18px; }
                ${scopeSel} .kbola-modalFooter { padding: 12px 14px; border-top: 1px solid rgba(0,0,0,0.06); background: rgba(0,0,0,0.01); }
                ${scopeSel} .kbola-pill { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 999px; font-weight: 900; font-size: 11px; border: 1px solid rgba(0,0,0,0.10); }
                ${scopeSel} .kbola-pillBrand { background: ${colors.textOnPrimary}; color: ${colors.primary}; }
                ${scopeSel} .kbola-infoCard { display: flex; gap: 12px; align-items: flex-start; border-radius: 14px; border: 1px solid rgba(var(--kbs-primary-rgb, 0,173,255), 0.22); background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.06); color: rgba(20,21,26,0.72); padding: 12px 14px; margin-bottom: 14px; font-size: 12px; font-weight: 700; }
                ${scopeSel} .kbola-infoIcon { color: ${colors.primary}; font-size: 18px; }
                ${scopeSel} .kbola-infoTitle, ${scopeSel} .kbola-uploadTitle, ${scopeSel} .kbola-sectionLabel { color: #14151a; font-size: 13px; font-weight: 900; }
                ${scopeSel} .kbola-uploadCard { border-radius: 14px; border: 1px solid rgba(0,0,0,0.08); background: rgba(0,0,0,0.015); padding: 12px; margin-bottom: 18px; }
                ${scopeSel} .kbola-uploadTop { margin-bottom: 10px; }
                ${scopeSel} .kbola-uploadTopRow { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
                ${scopeSel} .kbola-uploadActions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
                ${scopeSel} .kbola-templateBtn { border-radius: 10px; padding: 8px 10px; font-weight: 900; display: inline-flex; align-items: center; gap: 7px; background: #fff; color: ${colors.primary}; border: 1px solid ${colors.primary}; font-size: 12px; }
                ${scopeSel} .kbola-templateBtn:hover { background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.07); color: ${colors.primary}; }
                ${scopeSel} .kbola-uploadHint, ${scopeSel} .kbola-fileSubhint { color: rgba(20,21,26,0.58); font-size: 11px; font-weight: 700; margin-top: 3px; }
                ${scopeSel} .kbola-fileNative { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
                ${scopeSel} .kbola-fileShell { display: flex; align-items: center; gap: 10px; border-radius: 14px; border: 1.5px dashed ${colors.primary}; background: rgba(255,255,255,0.85); padding: 10px; }
                ${scopeSel} .kbola-fileBtn, ${scopeSel} .kbola-primaryBtn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 10px 12px; border-radius: 12px; background: ${colors.primary}; color: ${colors.textOnPrimary}; font-weight: 900; cursor: pointer; white-space: nowrap; border: 1px solid rgba(20,21,26,0.15); }
                ${scopeSel} .kbola-fileMeta { flex: 1; min-width: 0; }
                ${scopeSel} .kbola-fileName { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 800; color: ${colors.textOnPrimary}; background: ${colors.primary}; border: 1px solid rgba(0,0,0,0.10); padding: 6px 10px; border-radius: 999px; font-size: 12px; }
                ${scopeSel} .kbola-filePlaceholder { color: ${colors.primary}; font-weight: 700; font-size: 12px; }
                ${scopeSel} .kbola-fileClear { width: 38px; height: 38px; border-radius: 12px; border: 1px solid rgba(20,21,26,0.10); background: ${colors.primary}; color: ${colors.textOnPrimary}; display: flex; align-items: center; justify-content: center; cursor: pointer; }
                ${scopeSel} .kbola-sheetTabs { gap: 8px; }
                ${scopeSel} .kbola-sheetTab { border-radius: 999px; padding: 7px 11px; border: 1px solid rgba(0,0,0,0.10); background: rgba(0,0,0,0.02); color: rgba(20,21,26,0.72); font-weight: 800; font-size: 12px; }
                ${scopeSel} .kbola-importSummary { border-radius: 14px; padding: 12px 14px; border: 1px solid rgba(0,0,0,0.10); }
                ${scopeSel} .kbola-importSummarySuccess { background: rgba(25,135,84,0.08); color: #146c43; border-color: rgba(25,135,84,0.25); }
                ${scopeSel} .kbola-importSummaryWarning { background: rgba(220,53,69,0.06); color: #842029; border-color: rgba(220,53,69,0.25); }
                ${scopeSel} .kbola-importSummaryTitle { font-weight: 900; font-size: 13px; }
                ${scopeSel} .kbola-importSummaryStats { display: flex; flex-wrap: wrap; gap: 18px; margin-top: 7px; font-size: 12px; }
                .kbola-rowErrors { border: 1px solid rgba(220,53,69,0.25); border-radius: 14px; background: rgba(220,53,69,0.06); padding: 12px 14px; color: #842029; }
                .kbola-rowErrorsTitle { font-weight: 900; font-size: 13px; margin-bottom: 7px; }
                .kbola-rowErrorItem { display: flex; gap: 10px; font-size: 12px; padding-top: 5px; }
                .kbola-rowErrorItem strong { white-space: nowrap; }
                ${scopeSel} .kbola-sheetTabActive { background: ${colors.primary}; border-color: ${colors.primary}; color: ${colors.textOnPrimary}; }
                ${scopeSel} .kbola-mappingTableWrap { border-radius: 16px; overflow: auto; border: 1px solid rgba(0,0,0,0.08); box-shadow: 0 10px 26px rgba(0,0,0,0.06); }
                ${scopeSel} .kbola-previewHeader { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 18px 0 8px; }
                ${scopeSel} .kbola-previewHeader > span { color: rgba(20,21,26,0.55); font-size: 11px; font-weight: 700; }
                ${scopeSel} .kbola-mappingTable { min-width: 700px; }
                ${scopeSel} .kbola-thead th { background: rgba(0,0,0,0.02); color: rgba(20,21,26,0.65); font-size: 12px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.02em; padding: 12px 14px; white-space: nowrap; }
                ${scopeSel} .kbola-tbody td { border-top: 1px solid rgba(0,0,0,0.06); padding: 10px 12px; vertical-align: middle; background: #fff; font-size: 13px; }
                ${scopeSel} .kbola-control { border-radius: 12px; border: 1px solid rgba(0,0,0,0.10); font-weight: 800; font-size: 13px; padding: 9px 10px; height: 44px; box-shadow: none; min-width: 240px; }
                ${scopeSel} .kbola-control:focus { outline: none; box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18); border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.75); }
                ${scopeSel} .kbola-requiredAlert { border-radius: 14px; padding: 11px 14px; margin-top: 14px; font-size: 12px; font-weight: 800; }
                ${scopeSel} .kbola-requiredAlertWarning { color: rgba(125,83,0,0.95); border: 1px solid rgba(255,193,7,0.30); background: rgba(255,193,7,0.10); }
                ${scopeSel} .kbola-requiredAlertSuccess { color: rgba(25,110,70,0.95); border: 1px solid rgba(25,135,84,0.20); background: rgba(25,135,84,0.07); }
                ${scopeSel} .kbola-secondaryBtn { border-radius: 12px; font-weight: 900; padding: 10px 14px; }
                @media (max-width: 576px) { ${scopeSel} .modal-dialog { width: calc(100vw - 1rem); margin: 0.5rem auto; } ${scopeSel} .kbola-fileShell { align-items: stretch; flex-direction: column; } ${scopeSel} .kbola-fileBtn { width: 100%; } ${scopeSel} .kbola-fileClear { align-self: flex-end; } }
            `}</style>
        </>
    );
}
